/**
 * Feature-gate and catalog-pointer READ primitives (FN experience seam,
 * slice 3).
 *
 * These are the hooks the app's surfaces consume the served experience
 * through. DELIBERATELY UNWIRED here: none of this app's current nav
 * items or routes corresponds to a served gate key (hidden_steps carries
 * wizard step ids, disabled_features carries feature keys like VIP's
 * sow_generation), and inventing a mapping to prove the hooks would gate
 * real UI on a guess. The wiring belongs to the modules that own those
 * features, when they land — see the handoff doc
 * (docs/consolidation/lanes/lead-b/fn-experience-handoff-2026-08-17.md).
 *
 * FAILURE POSTURE — the PLATFORM's, not a new one: when the bootstrap is
 * unavailable, everything is VISIBLE/ENABLED. This mirrors the Vite app's
 * documented rule ("bootstrap failure is deliberately default-visible",
 * SurfaceStepRoute; its ThemeProvider's fallback is 'defaults to
 * VISIBLE'). Gates are SUBTRACTIVE brand configuration, not
 * authorization — the server enforces anything that matters regardless
 * of what a browser renders — and the two front ends must fail the same
 * way or a flaky bootstrap makes them disagree about what exists.
 */
import { useExperience } from "./ExperienceProvider";

/** True unless the READY experience explicitly disables the feature. */
export function useFeatureEnabled(feature: string): boolean {
  const exp = useExperience();
  if (exp.status !== "ready") return true;
  return !exp.featureGates.disabledFeatures.includes(feature);
}

/** True only when the READY experience explicitly hides the step. */
export function useStepHidden(step: string): boolean {
  const exp = useExperience();
  if (exp.status !== "ready") return false;
  return exp.featureGates.hiddenSteps.includes(step);
}

/**
 * The experience's catalog pointer for a scope, or the platform default.
 * The Vite twin's precedent is useCatalogProjectType (ThemeProvider):
 * VIP's served map is { ucaas: "vip_ucaas", ccaas: "vip_ccaas" }, ZW's is
 * {} = platform default. This is what lets one deployment serve two
 * PRODUCTS, not just two paint jobs (the b43b9ed pointer).
 */
export function useCatalogProjectType(
  scope: string,
  platformDefault: string,
): string {
  const exp = useExperience();
  if (exp.status !== "ready") return platformDefault;
  const map = exp.catalog["project_type_map"];
  if (!map || typeof map !== "object" || Array.isArray(map)) {
    return platformDefault;
  }
  const value = (map as Record<string, unknown>)[scope];
  return typeof value === "string" && value ? value : platformDefault;
}
